import posthog, { type CaptureResult } from "posthog-js"
import { router } from "@inertiajs/react"

export type PostHogSettings = {
  token: string
  apiHost: string
  uiHost: string
}

type AuthProp = {
  user: {
    id: number
    name: string
    role: string
    organization: { slug: string } | null
  } | null
} | null

/**
 * The one shared prop this module reads. The index signature is what lets
 * Inertia's own page props be passed straight in — without it TypeScript's
 * weak-type check rejects an all-optional target.
 */
type PageProps = { auth?: AuthProp;[key: string]: unknown }

/**
 * PostHog product analytics + session replay, app-wide.
 *
 * This runs everywhere, unlike GA4 in `lib/analytics.ts`, which is pinned to
 * MarketingLayout so it can never see an authenticated path. PostHog gets the
 * whole app because the questions worth asking — where drivers stall in the
 * packet, which requirement they abandon — only have answers on the
 * authenticated side. That trade is only safe because of the four layers
 * below. Read them before loosening any of them.
 *
 * 1. NOTHING TYPED IS RECORDED, AND IDENTIFYING TEXT IS MASKED PER ELEMENT.
 *    `maskAllInputs` covers every `<input>`, which is where SSN, DOB, licence
 *    numbers and addresses are entered — that half is automatic and total.
 *    Rendered text is *not* masked wholesale, so replays stay readable enough
 *    to be worth watching: nav, headings, buttons, status and instructions all
 *    come through. The cost of that is a rule someone has to follow — text
 *    that identifies a person carries `ph-mask`, via `components/private.tsx`.
 *    rrweb applies all of it in the browser, so masked content never leaves.
 *
 *    This is the layer most easily eroded: a new admin page that renders a
 *    driver's name and forgets `<Private>` will record it. Layer 4 is the
 *    backstop, and is the reason it stays broad.
 *
 * 2. AUTOCAPTURE CARRIES NO CONTENT. `mask_all_text` and
 *    `mask_all_element_attributes` stop autocaptured clicks from shipping
 *    `textContent` and attributes. Without them, clicking a row in the drivers
 *    table would send that driver's name as an event property — replay masking
 *    would not have covered it, because that is analytics, not replay.
 *
 * 3. THE DOCUMENT BITMAP IS BLOCKED, NOT MASKED. The signing viewer renders
 *    each PDF page as an `<img>` (see `PdfPageViewer` in
 *    `components/signing-form.tsx`). Text masking does nothing to a bitmap, so
 *    that image carries `ph-no-capture` — PostHog's default `blockClass` —
 *    and replays as an empty box of the same size. Blocking the one element
 *    rather than the whole page keeps the surrounding funnel observable, which
 *    is what the Replay Vision "broken experiences" scanner watches.
 *
 * 4. THE WORST PAGES NEVER RECORD AT ALL. See NEVER_RECORD.
 *
 * Layer 4 exists because layer 1 is not entirely ours to control: PostHog lets
 * `maskAllInputs`, `maskTextSelector` and `blockSelector` be overridden from
 * the project's replay settings in the PostHog UI. Someone relaxing masking in
 * a web dashboard must not thereby start recording SSNs, so the pages holding
 * them are stopped locally, where no remote config reaches.
 *
 * Does nothing without a project token, which is how development and test stay
 * out of the project.
 */

/**
 * Paths that are never recorded, matched against `location.pathname`.
 *
 * These render regulated identifiers as their whole reason for existing — SSN,
 * banking details, DOB, licence numbers, immigration status — or show a
 * completed document rather than a blank template. Both sides are listed: an
 * admin reviewing a packet sees the same content the driver typed.
 *
 * `/packet/sign` and `/packet/i9` are deliberately absent. They're the funnel
 * worth watching, they show a blank template rather than a filled document,
 * and layers 1–3 already cover what's on them.
 */
const NEVER_RECORD: RegExp[] = [
  /^\/packet\/payroll\//,
  /^\/packet\/document\//,
  /^\/drivers\/\d+\/countersigns\//,
  /^\/drivers\/\d+\/documents\//,
  /^\/drivers\/\d+\/payroll_submissions\//,
  /^\/drivers\/\d+\/i9s\//
]

/**
 * Whether *we* paused the recorder, as opposed to it never having started.
 *
 * The distinction matters: a bare `startSessionRecording()` would force
 * recording on even where the project has replay switched off or sampled away.
 * Resuming only what this module paused leaves those settings authoritative.
 */
let pausedByPolicy = false

/** The distinct id currently identified, so repeat visits don't re-identify. */
let identifiedAs: string | null = null

/** The message shape of the known extension rejection, and nothing wider. */
const EXTENSION_REJECTION = /Object Not Found Matching Id:\d+, MethodName:/

/**
 * Drop one known browser-extension rejection before it leaves the browser. A
 * layer beside the masking layers above: it protects error-tracking signal, not
 * privacy.
 *
 * A browser extension (or an email link scanner) injects a script that raises
 * `Non-Error promise rejection captured with value: Object Not Found Matching
 * Id:<n>, MethodName:<name>`. `defaults: "2026-06-25"` turns on exception
 * autocapture, so this lands in error tracking with `synthetic: true` and no
 * stack frames — from the visitor's machine, never from our app.
 *
 * The match stays narrow on purpose. Only a `$exception` event with a single
 * `$exception_list` entry qualifies, and only when that entry has no stack
 * frames and a value that matches `EXTENSION_REJECTION`. A real non-Error
 * rejection from our own code still reaches us. Every other event passes
 * through unchanged.
 */
function dropExtensionNoise(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== "$exception") return event

  const list = event.properties?.$exception_list
  if (!Array.isArray(list) || list.length !== 1) return event

  const [exception] = list
  const frames = exception?.stacktrace?.frames
  if (Array.isArray(frames) && frames.length > 0) return event

  const value = exception?.value
  if (typeof value === "string" && EXTENSION_REJECTION.test(value)) return null

  return event
}

export function startPostHog(settings: PostHogSettings, initialProps: PageProps) {
  posthog.init(settings.token, {
    api_host: settings.apiHost,
    // Where replay/flag links in the SDK point. Without it they'd aim at the
    // ingestion host, which doesn't serve the UI.
    ui_host: settings.uiHost,
    defaults: "2026-06-25",

    // Inertia navigates with the History API and never reloads the document,
    // so the single pageview fired at boot would otherwise be the only one.
    capture_pageview: "history_change",

    // Anonymous marketing visitors don't need person profiles; identified
    // users get one from the `identify` below.
    person_profiles: "identified_only",

    // Layer 2. These govern analytics events; replay is masked separately.
    mask_all_text: true,
    mask_all_element_attributes: true,
    mask_personal_data_properties: true,
    before_send: [dropExtensionNoise, stripPageTitle],

    // Layer 1. No maskTextSelector: rendered text is masked per element via
    // `ph-mask` (see components/private.tsx), not wholesale. This matches the
    // project's own replay masking config in PostHog, which also sets
    // maskAllInputs and nothing else — local and remote agree, so there's no
    // override to be surprised by.
    session_recording: {
      maskAllInputs: true
    }
  })

  syncIdentity(initialProps.auth)
  applyRecordingPolicy()

  // `navigate` carries the new page's props, which is where a login or logout
  // shows up. Registering once here rather than in a layout means no page can
  // be added that quietly skips it.
  router.on("navigate", (event) => {
    syncIdentity((event.detail.page.props as PageProps).auth)
    applyRecordingPolicy()
  })
}

function applyRecordingPolicy() {
  const blocked = NEVER_RECORD.some((pattern) => pattern.test(window.location.pathname))

  if (blocked && !pausedByPolicy) {
    posthog.stopSessionRecording()
    pausedByPolicy = true
  } else if (!blocked && pausedByPolicy) {
    posthog.startSessionRecording()
    pausedByPolicy = false
  }
}

/**
 * Drop the page title from every event.
 *
 * Several pages title themselves after the person they're about — the driver
 * detail page is `<Head title={driver.full_name} />` — and posthog-js copies
 * `document.title` onto pageviews as `title`. That's an event property, so
 * neither `ph-mask` nor any replay setting touches it; it would arrive at
 * PostHog as a plain name. `$pathname` already identifies the page, and it
 * carries an id rather than a name, so nothing analytically useful is lost.
 */
function stripPageTitle(event: CaptureResult | null): CaptureResult | null {
  if (event?.properties) delete event.properties.title
  return event
}

/**
 * Reduce a display name to a first name and a last initial: "Dale Hoffman"
 * becomes "Dale H." Middle names are dropped rather than initialled, so
 * "Mary Jane Watson" is "Mary W."
 *
 * Enough for a human to recognise the person they're already investigating,
 * not enough to be a roster of who drives for which carrier if the analytics
 * project is ever exposed.
 */
function abbreviateName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ""
  if (parts.length === 1) return parts[0]
  return `${parts[0]} ${parts[parts.length - 1][0]}.`
}

/**
 * Tie events to a stable person.
 *
 * The distinct id stays `user_<id>`, never the name: names collide between
 * carriers and change on marriage or correction, either of which would merge
 * two drivers into one person or split one across two. The abbreviated name
 * rides along as a person property, which is what PostHog resolves for
 * display — so the UI reads "Dale H." while identity stays anchored to the id.
 *
 * Deliberately no email, no full name, and nothing encrypted at rest: person
 * properties are the one channel masking cannot cover, because we are the ones
 * sending them.
 */
function syncIdentity(auth: AuthProp | undefined) {
  const user = auth?.user ?? null

  if (!user) {
    // Signed out. Reset breaks the link between this browser and the person,
    // so the next driver on a shared depot machine starts clean.
    if (identifiedAs) {
      posthog.reset()
      identifiedAs = null
    }
    return
  }

  const distinctId = `user_${user.id}`
  if (distinctId === identifiedAs) return

  // A different user in the same browser — a kiosk hand-off, or an admin
  // ending an impersonation session. Without the reset the two would merge.
  if (identifiedAs) posthog.reset()

  posthog.identify(distinctId, {
    name: abbreviateName(user.name),
    role: user.role,
    organization: user.organization?.slug ?? null
  })
  if (user.organization) posthog.group("organization", user.organization.slug)

  identifiedAs = distinctId
}
